import { Chip, IconButton, Stack, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

/**
 * Close button for educational section
 */
export const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  flexShrink: 0,
  position: 'absolute',
  right: theme.spacing(2),

  '& svg': {
    width: '14.17px',
    height: '14.17px',
  },

  [theme.breakpoints.down(600)]: {
    position: 'static',
    marginLeft: 'auto',
  },
}));

/**
 * Educational header with top offset
 */
export const StyledRREducationalHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  height: '32px',
  padding: theme.spacing(0, 2),
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: '1 0 0',
  position: 'relative',

  '& .MuiButton-root': {
    whiteSpace: 'normal',
    textAlign: 'center',
  },

  [theme.breakpoints.down(600)]: {
    height: 'auto',
    marginTop: theme.spacing(2),
    flexWrap: 'nowrap',

    '& .MuiButton-root': {
      marginBottom: 0,
      whiteSpace: 'normal',
      textAlign: 'center',
      alignItems: 'flex-start',

      '& .MuiButton-startIcon': {
        marginTop: theme.spacing(0.5),
        marginRight: 0,
        marginLeft: 0,
      },
    },
  },
}));

/**
 * Educational content stack with themed gap
 */
export const StyledEducationalContentStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(4.5),
  padding: '5px',
}));

/**
 * Main card wrapper with border and padding
 */
export const StyledCard = styled(Stack)(({ theme }) => ({
  flex: 1,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.customShape.borderRadiusXLarge,
  padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
  gap: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}));

/**
 * Header container - flexbox for title, info icon, and execution gap tag
 */
export const StyledHeaderContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

/**
 * Title container with icon
 */
export const StyledTitleContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

/**
 * Title text styling
 */
export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.08px',
  color: theme.palette.text.primary,
}));

/**
 * Execution Gap badge (red/green bordered tag)
 */
interface ExecutionGapTagProps {
  isNegative?: boolean;
}

export const StyledExecutionGapTag = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isNegative',
})<ExecutionGapTagProps>(({ theme, isNegative }) => ({
  height: theme.spacing(6),
  padding: theme.spacing(1, 3),
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  lineHeight: 1.5,
  backgroundColor: isNegative
    ? alpha(theme.palette.error.main, 0.1)
    : alpha(theme.palette.success.accent, 0.1),
  border: `1px solid ${
    isNegative ? theme.palette.error.main : theme.palette.success.accent
  }`,
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,

  '& .MuiChip-label': {
    padding: 0,
  },
}));

/**
 * Subtitle text styling
 */
export const StyledSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(20),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.03px',
  color: theme.palette.text.secondary,

  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(16),
  },
}));

/**
 * Divider line
 */
export const StyledDivider = styled('div')(({ theme }) => ({
  width: '100%',
  height: 1,
  backgroundColor: theme.palette.divider,
}));

/**
 * Content row - horizontal layout for chart and stats/legend
 */
export const StyledContentRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '21px',
  alignSelf: 'stretch',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

/**
 * Donut chart container with stroke reset for pie arcs
 */
export const StyledChartContainer = styled(Stack)(() => ({
  alignItems: 'center',
  flexShrink: 0,

  '& .MuiPieArc-root': {
    stroke: 'none',
  },
}));

/**
 * Stats and legend wrapper - fills remaining row space
 */
export const StyledStatsLegendContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  gap: theme.spacing(2),
}));

/**
 * Stats row container (Med. Planned + Med. Realized) with space-between
 */
export const StyledStatsRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
}));

/**
 * Individual stat item
 */
export const StyledStatItem = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.5),
}));

/**
 * Stat label (Med. Planned / Med. Realized)
 */
export const StyledStatLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.secondary,
}));

/**
 * Stat value (2R / 1.3R)
 */
export const StyledStatValue = styled(Typography)(({ theme }) => ({
  fontSize: '24px !important',
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '0.09px',
  color: theme.palette.text.primary,
}));

/**
 * Legend container (vertical list)
 */
export const StyledLegendContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
}));

/**
 * Individual legend item row
 */
export const StyledLegendItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

/**
 * Legend color dot
 */
interface LegendDotProps {
  dotColor: string;
}

export const StyledLegendDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})<LegendDotProps>(({ theme, dotColor }) => ({
  width: theme.spacing(3.25),
  height: theme.spacing(3.25),
  borderRadius: theme.customShape.borderRadiusFull,
  backgroundColor: dotColor,
  flexShrink: 0,
}));

/**
 * Legend label text
 */
export const StyledLegendLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.secondary,
  flex: 1,
}));

/**
 * Legend percentage value (right-aligned)
 */
export const StyledLegendPercentage = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.primary,
}));

/**
 * Center label text for donut chart (SVG coordinates use px)
 */
export const StyledCenterTextTop = styled('text')(({ theme }) => ({
  fill: theme.palette.text.secondary,
  textAnchor: 'middle' as const,
  dominantBaseline: 'central' as const,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
}));

export const StyledCenterTextBottom = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle' as const,
  dominantBaseline: 'central' as const,
  fontSize: theme.typography.pxToRem(35),
  fontWeight: 700,
}));

/**
 * Info icon button with no padding
 */
export const StyledInfoIconButton = styled(IconButton)({
  padding: 0,
});

/**
 * Loading state container
 */
export const StyledLoadingContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: theme.spacing(75),
}));

/**
 * Educational body text with primary color override
 */
export const StyledRREducationalText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
}));

/**
 * Educational bullet list with primary color override
 */
export const StyledRRBulletList = styled('ul')(({ theme }) => ({
  margin: theme.spacing(1, 0, 0, 2),
  padding: 0,
  paddingLeft: theme.spacing(4),
  listStyleType: 'disc',

  '& li': {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
}));

/**
 * Educational quote with primary color override
 */
export const StyledRRQuote = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(32),
  fontWeight: 400,
  fontStyle: 'italic',
  lineHeight: 1.6,
  color: theme.palette.text.primary,
}));
